import { deleteDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { getFirebaseStorage, getFirestoreDb } from './firebaseRemoteSync';
import { decodeBlobToAudioBuffer, isIosLikeDevice } from './voiceNoteAudio';

export function isGuestVoiceNotesConfigured() {
  return Boolean(getFirestoreDb() && getFirebaseStorage());
}

async function blobDurationSec(blob) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC();
  try {
    const buf = await decodeBlobToAudioBuffer(ctx, blob);
    return Number.isFinite(buf.duration) ? buf.duration : 0;
  } finally {
    await ctx.close().catch(() => {});
  }
}

/**
 * Upload blob to Storage and create Firestore metadata for admins.
 * @param {Blob} blob
 * @param {import('./voiceNoteUrlContext').VoiceNoteSubmissionContext} [submissionContext] optional fields from URL (firstName, email, source)
 * @returns {Promise<{ id: string; storagePath: string }>}
 */
export async function submitGuestVoiceNote(blob, submissionContext = {}) {
  const db = getFirestoreDb();
  const storage = getFirebaseStorage();
  if (!db || !storage) {
    throw new Error('Firebase is not configured');
  }
  const id = crypto.randomUUID();
  const storagePath = `guestVoiceNotes/${id}`;
  const storageRef = ref(storage, storagePath);
  const fallbackType = isIosLikeDevice() ? 'audio/mp4' : 'audio/webm';
  const mimeType = blob.type && blob.type.startsWith('audio/') ? blob.type : fallbackType;

  await uploadBytes(storageRef, blob, { contentType: mimeType });

  let durationSec = 0;
  try {
    durationSec = await blobDurationSec(blob);
  } catch {
    durationSec = 0;
  }
  if (!Number.isFinite(durationSec) || durationSec < 0) durationSec = 0;
  if (durationSec > 600) durationSec = 600;

  /** @type {Record<string, unknown>} */
  const docData = {
    storagePath,
    mimeType,
    durationSec,
    // Server time avoids client clock skew failing Firestore create rules.
    submittedAt: serverTimestamp(),
  };
  if (submissionContext.firstName && typeof submissionContext.firstName === 'string') {
    docData.firstName = submissionContext.firstName;
  }
  if (submissionContext.email && typeof submissionContext.email === 'string') {
    docData.email = submissionContext.email;
  }
  if (submissionContext.source && typeof submissionContext.source === 'string') {
    docData.source = submissionContext.source;
  }

  await setDoc(doc(db, 'guestVoiceNotes', id), docData);

  return { id, storagePath };
}

/**
 * Admin cleanup: remove Storage object then Firestore doc.
 * @param {string} id
 * @param {string} storagePath from doc (e.g. guestVoiceNotes/uuid)
 */
export async function deleteGuestVoiceNoteAdmin(id, storagePath) {
  const db = getFirestoreDb();
  const storage = getFirebaseStorage();
  if (!db || !storage) throw new Error('Firebase is not configured');
  const path = storagePath || `guestVoiceNotes/${id}`;
  try {
    await deleteObject(ref(storage, path));
  } catch {
    /* object may already be gone */
  }
  await deleteDoc(doc(db, 'guestVoiceNotes', id));
}

/** @param {import('firebase/storage').FirebaseStorage} storage */
export async function getGuestVoiceNoteDownloadUrl(storage, storagePath) {
  return getDownloadURL(ref(storage, storagePath));
}
